import type { Quest } from '../types';

// Hardcoded main quests by era. Each one is `required: true` for v1 — all must
// be done before The Overseer will validate the era. User can mark them done
// manually in the QuestBoard (checkbox).
//
// Each entry carries:
//   phase     = era index 0..5 (kept as `phase` for legacy compat — drives QuestBoard gating)
//   era       = same as phase, explicit alias
//   mgtPhase  = MGT-413 phase index 0..12 (finer-grained than era)
//   deliverables = expected file paths under agent-data/. Wires to stressTests/era<N>.ts.
//   xp        = pacing currency reward
//   source    = always 'phase-template' for these baseline quests
const QUEST_LIST: Omit<Quest, 'done'>[] = [
  // ── Era 0 — Identity (MGT-413 Phase 0: Founder readiness) ───────────────────
  {
    id: 'p0-founder-identity',
    era: 0,
    mgtPhase: 0,
    title: 'Take the founder identity test',
    description:
      'Discover your founder archetype — Darwinian, Communitarian, or Missionary. 15 statements, ~3 minutes. Your archetype shapes every decision the bunker will guide you through.',
    assignedAgentId: 'overseer',
    starterPrompt:
      'Administer the founder identity test. First introduce what the test is, what the three archetypes mean, and why knowing your archetype matters — then present the 15 statements in batches of 5, collect my 1-7 ratings, compute the three scores, and declare my dominant archetype with its shadow risk.',
    required: true,
    deliverables: ['scribe/founder-identity.md'],
    reportPdf: 'overseer/founder-identity-report.pdf',
    xp: 250,
    source: 'phase-template',
  },
  {
    id: 'p0-phase-zero-ritual',
    era: 0,
    mgtPhase: 0,
    title: 'Complete Phase 0 founder ritual',
    description: 'Articulate your thesis, explore 3 domains, declare venture type, and codify your edge. The Overseer guides the full ritual.',
    assignedAgentId: 'overseer',
    starterPrompt:
      'Complete the Phase 0 founder ritual with me. Guide me through: (1) defining the problem I am uniquely positioned to solve, (2) naming 3 domains I will explore, (3) choosing my venture type, (4) codifying my skills and identifying my critical gaps, (5) capturing my available resources — network (who I can reach), infra (team/stack), capital (runway/pilot budget), domain access (segments I reach privileged), and hours/week. At the end, generate the Era 0 Founder Brief report. Save the resources as ../scribe/founder-resources.md with ## Réseau, ## Infra, ## Capital, ## Accès domaine, ## Temps/semaine sections.',
    required: true,
    deliverables: ['scribe/thesis.md', 'scribe/domains.md', 'scribe/venture-type.md', 'scribe/skills-map.md', 'scribe/founder-resources.md'],
    reportPdf: 'overseer/era0-founder-brief.pdf',
    xp: 500,
    source: 'phase-template',
  },

  // ── Era 1 — Atlas (MGT-413 Phases 1+2: Opportunity landscape + MOON) ────────
  {
    id: 'p1-map-wasteland',
    era: 1,
    mgtPhase: 1,
    title: 'Map the wasteland',
    description: 'Scan the competitive landscape — and find the white space. Who are the rivals, and what do they leave unsolved?',
    assignedAgentId: 'scout',
    starterPrompt:
      'Run a recon pass on the competitive landscape for my product idea. List direct, adjacent, and indirect competitors with one-line summaries. For each, name what they do NOT solve and where they leave the segment underserved — the white space is where the opportunity lives. Use WebSearch. Save as agent-data/scout/competitors.md.',
    required: true,
    deliverables: ['scout/competitors.md'],
    xp: 250,
    source: 'phase-template',
  },
  {
    id: 'p1-segments',
    era: 1,
    mgtPhase: 1,
    title: 'Identify 30 opportunities',
    description: 'Generate ≥30 raw opportunities across multiple domains. Quantity before quality.',
    assignedAgentId: 'scout',
    starterPrompt:
      'Generate ≥30 raw opportunities, then deep-dive the ones tied to my thesis. An opportunity = {a reachable segment} + {a painful urgent problem they solve badly today} + {their current alternative} — never a market or a technology alone. Use WebSearch in BOTH tempos. TEMPO A (breadth): ~4-6 grouped WebSearch queries per problem-space on current pain signals / 2026 trends, extract ~20 lightweight opportunities (who / problem / current alternative each), then tell me to take them to The Elder to challenge my thesis. TEMPO B (depth): per-opportunity WebSearch on the cluster tied to my thesis in agent-data/scribe/thesis.md — ~10 fully scored with a source link, why-now, urgency 1-5, WTP, and access difficulty 1-5 scored against agent-data/scribe/founder-resources.md. Write all as ## OPP-NNN sections into a SINGLE file agent-data/scout/opportunities.md (≥30 sections total).',
    required: true,
    deliverables: ['scout/opportunities.md'],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p1-synthesize-signal',
    era: 1,
    mgtPhase: 2,
    title: 'MOON + pick beachhead',
    description: 'Run the Market Opportunity Navigator and pick a beachhead opportunity.',
    assignedAgentId: 'overseer',
    starterPrompt:
      'Given the opportunities in agent-data/scout/opportunities.md, run the Market Opportunity Navigator: 1) Market Opportunity Set, 2) Attractiveness Map, 3) Agile Focus Strategy. Save the analysis at agent-data/scribe/moon.md, the attractiveness map at agent-data/scribe/attractiveness-map.md, the chosen beachhead at agent-data/scribe/beachhead.md, and the backup option at agent-data/scribe/backup-option.md.',
    required: true,
    deliverables: [
      'scribe/moon.md',
      'scribe/attractiveness-map.md',
      'scribe/beachhead.md',
      'scribe/backup-option.md',
    ],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p1-pitch-one',
    era: 1,
    mgtPhase: 2,
    title: 'Assemble Pitch 1',
    description:
      'Synthesise the 3 strongest opportunities, their evaluation, and the next steps for the chosen beachhead into a 6-slide pitch.',
    assignedAgentId: 'scribe',
    starterPrompt:
      'Assemble Pitch 1 from agent-data/scout/opportunities.md, scribe/attractiveness-map.md and scribe/beachhead.md. 6 sections: (1) the 3 opportunities, (2) evaluation summary, (3) chosen beachhead + why, (4) the problem, (5) the solution sketch, (6) next steps. Outcome-first, no jargon. Save as agent-data/scribe/pitch-1.md. Then call generate_slide_deck to render the YC-minimal deck (pitch-1.pptx / pitch-2.pptx / pitch-3.pptx matching the pitch number).',
    required: true,
    deliverables: ['scribe/pitch-1.md'],
    xp: 500,
    source: 'phase-template',
  },

  // ── Era 2 — Truth (MGT-413 Phases 3+4: Problem validation + selection) ─────
  {
    id: 'p2-interview-script',
    era: 2,
    mgtPhase: 3,
    title: 'Prep interview script',
    description: 'The 7 Mom Test questions tailored to the beachhead segment.',
    assignedAgentId: 'investigator',
    starterPrompt:
      'Help me write 5-7 customer interview questions targeting past behavior, not opinions. Mom Test rules. Tailor them to the beachhead segment in agent-data/scribe/beachhead.md. Save as agent-data/investigator/interview-script.md.',
    required: true,
    deliverables: ['investigator/interview-script.md'],
    xp: 250,
    source: 'phase-template',
  },
  {
    id: 'p2-run-interviews',
    era: 2,
    mgtPhase: 3,
    title: 'Run ≥20 interviews',
    description: 'Conduct, transcribe, log per the 11-section template. Target ≥60% pain ≥4.',
    assignedAgentId: 'investigator',
    starterPrompt:
      'Help me log my customer interviews following the 11-section template from your persona. I will paste verbatims; you create one file per interview at agent-data/investigator/interviews/<date>-<slug>.md with full frontmatter (date, name, role, company, segment, interview_type problem|expert|buyer|observation, pain_severity 1-5, evidence_score 1-5, is_buyer, willing_to_review_prototype). Refuse to summarize before 5 are filed.',
    required: true,
    deliverables: ['investigator/interviews/*.md'],
    xp: 1000,
    source: 'phase-template',
  },
  {
    id: 'p2-index-decisions',
    era: 2,
    mgtPhase: 3,
    title: 'Index decisions + personas + memo',
    description: 'Decision log, customer + buyer personas, opportunity memo (12 sections).',
    assignedAgentId: 'scribe',
    starterPrompt:
      'From the interviews in agent-data/investigator/interviews/, produce: 1) the decision log at agent-data/scribe/decision-log.md, 2) the customer persona at agent-data/scribe/customer-persona.md, 3) the buyer persona at agent-data/scribe/buyer-persona.md, 4) the 12-section Opportunity Memo at agent-data/scribe/opportunity-memo.md. Mark [unverified] where claims lack evidence files.',
    required: true,
    deliverables: [
      'scribe/decision-log.md',
      'scribe/customer-persona.md',
      'scribe/buyer-persona.md',
      'scribe/opportunity-memo.md',
    ],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p2-baseline-kpis',
    era: 2,
    mgtPhase: 4,
    title: 'Compute baseline KPIs',
    description: 'Define the 3-5 numbers that will tell us if we are alive.',
    assignedAgentId: 'analyst',
    starterPrompt:
      'Define 3-5 baseline KPIs for this product. For each: what it measures, how to compute it, the threshold under which we are dying. Save as agent-data/analyst/baseline-kpis.md.',
    required: true,
    deliverables: ['analyst/baseline-kpis.md'],
    xp: 250,
    source: 'phase-template',
  },
  {
    id: 'p2-problem-solution-hypothesis',
    era: 2,
    mgtPhase: 4,
    title: 'Commit your problem→solution hypothesis',
    description:
      'Turn the interview evidence into one falsifiable bet: the validated problem, the solution concept, why it beats the alternatives, and the signal that would prove you wrong — before a line of the MVP is built.',
    assignedAgentId: 'overseer',
    starterPrompt:
      'Using the validated pain in agent-data/investigator/interviews/, the personas and opportunity memo in agent-data/scribe/, and the white-space gaps in agent-data/scout/competitors.md, help me commit a single falsifiable problem→solution hypothesis. State it as: "[segment] suffers [validated problem]. We bet [solution concept] beats [existing alternatives] because [the edge we understand that they do not]. We are wrong if [kill signal]." Mark [unverified] where a claim lacks an interview quote. Save as agent-data/scribe/problem-solution-hypothesis.md.',
    required: true,
    deliverables: ['scribe/problem-solution-hypothesis.md'],
    xp: 500,
    source: 'phase-template',
  },

  // ── Era 3 — Forge (MGT-413 Phases 5+6: MVP + Business model) ───────────────
  {
    id: 'p3-scaffold-mvp',
    era: 3,
    mgtPhase: 5,
    title: 'Scaffold MVP',
    description: 'Pick MVP type (concierge / wizard-of-oz / prototype / sim / landing / pilot). Spec it.',
    assignedAgentId: 'tinkerer',
    starterPrompt:
      'Walk me through the smallest MVP we could build for the beachhead in agent-data/scribe/beachhead.md. The MVP must test the bet committed in agent-data/scribe/problem-solution-hypothesis.md — restate that critical hypothesis and its kill signal. Pick one of the 6 MVP types from your persona. Save the spec at agent-data/tinkerer/mvp-spec.md.',
    required: true,
    deliverables: ['tinkerer/mvp-spec.md'],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p3-wire-data-flow',
    era: 3,
    mgtPhase: 5,
    title: 'Build the smallest runnable MVP',
    description: 'Build the smallest version a real prospect can experience — code, prototype, process, or rig per your venture playbook.',
    assignedAgentId: 'tinkerer',
    starterPrompt:
      'Build the smallest version of the MVP defined in agent-data/tinkerer/mvp-spec.md that a real prospect can actually experience. Follow the venture playbook in your SESSION CONTEXT: software → a thin runnable slice; deeptech → a lab proof; services → a repeatable process; marketplace → hand-matched liquidity. Show me each step.',
    required: true,
    deliverables: [],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p3-stress-test-mvp',
    era: 3,
    mgtPhase: 5,
    title: 'Stress-test the MVP',
    description: 'Adversarial review of the MVP artefact (code, prototype, process, or rig). Find what breaks before prospects do.',
    assignedAgentId: 'auditor',
    starterPrompt:
      'Stress-test the MVP we just built. Inspect whatever artefact it is — code, clickable prototype, service process, or lab rig per the venture playbook — and find what breaks or fails to deliver value, ranked by severity. Be ruthless. Save the report as agent-data/auditor/mvp-stress-report.md.',
    required: true,
    deliverables: ['auditor/mvp-stress-report.md'],
    xp: 250,
    source: 'phase-template',
  },
  {
    id: 'p3-prospect-feedback',
    era: 3,
    mgtPhase: 5,
    title: 'Log ≥5 prospect feedback sessions',
    description: 'Put the MVP in front of ≥5 real prospects and log each session — behavior observed, not opinions collected.',
    assignedAgentId: 'investigator',
    starterPrompt:
      'For every prospect who tries the MVP, help me log a feedback session at agent-data/investigator/feedback/<date>-<name>.md. Mom Test rules apply: what they DID with it, where they got stuck, whether the pain actually shrank (1-5 before/after), and what they committed to next (energy ladder rung). Refuse to record "they liked it" without an observed behavior.',
    required: true,
    deliverables: ['investigator/feedback/*.md'],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p3-document-architecture',
    era: 3,
    mgtPhase: 6,
    title: 'Business model canvas + value prop',
    description: 'Lock the business model + value proposition before scaling sales.',
    assignedAgentId: 'scribe',
    starterPrompt:
      'Help me lock the Business Model Canvas at agent-data/scribe/business-model-canvas.md and the Value Proposition statement at agent-data/scribe/value-proposition.md (using the Phase 6 template: For X who faces Y, our Z helps them W by mechanism, unlike alternative, because differentiator).',
    required: true,
    deliverables: ['scribe/business-model-canvas.md', 'scribe/value-proposition.md'],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p3-moat-analysis',
    era: 3,
    mgtPhase: 6,
    title: 'Map your moat (VRIN)',
    description:
      'Assess the strategic value of your resources: rarity, appropriability of returns, durability, and substitutability/imitability. What protects you once it works?',
    assignedAgentId: 'scribe',
    starterPrompt:
      'Help me map our competitive moat using the strategic-value-of-resources framework (Collis/Montgomery). For each key resource/ability list: rarity, appropriability of returns, durability, substitutability, replicability/scalability. Conclude with the 1-2 resources that are genuinely defensible and how we deepen them. Save as agent-data/scribe/moat-analysis.md.',
    required: true,
    deliverables: ['scribe/moat-analysis.md'],
    xp: 250,
    source: 'phase-template',
  },
  {
    id: 'p3-pricing',
    era: 3,
    mgtPhase: 6,
    title: 'Validate pricing (value-based)',
    description:
      'Derive a value-based price from willingness-to-pay evidence in the interviews, not cost-plus guessing.',
    assignedAgentId: 'analyst',
    starterPrompt:
      'Using the willingness-to-pay signals in agent-data/investigator/interviews/, propose a value-based price: the economic gain we create for the customer, the share we can capture, the price point, and the evidence behind it. Contrast with a naive cost-plus number. Mark [unverified] where WTP lacks a concrete quote. Save as agent-data/analyst/pricing.md.',
    required: true,
    deliverables: ['analyst/pricing.md'],
    xp: 250,
    source: 'phase-template',
  },
  {
    id: 'p3-pitch-two',
    era: 3,
    mgtPhase: 6,
    title: 'Assemble Pitch 2',
    description:
      'Customer/beneficiary, offering, value proposition, resources/moat, business model, pricing, and next steps.',
    assignedAgentId: 'scribe',
    starterPrompt:
      'Assemble Pitch 2 from scribe/customer-persona.md, scribe/value-proposition.md, scribe/business-model-canvas.md, scribe/moat-analysis.md, analyst/pricing.md and tinkerer/mvp-spec.md. 6 sections: customer, offering, value proposition, business model + pricing, moat, next steps. Save as agent-data/scribe/pitch-2.md. Then call generate_slide_deck to render the YC-minimal deck (pitch-1.pptx / pitch-2.pptx / pitch-3.pptx matching the pitch number).',
    required: true,
    deliverables: ['scribe/pitch-2.md'],
    xp: 500,
    source: 'phase-template',
  },

  // ── Era 4 — Trade (MGT-413 Phases 7-9: GTM + Resources + Launch) ────────────
  {
    id: 'p4-icp-one-pager',
    era: 4,
    mgtPhase: 7,
    title: 'Draft ICP one-pager',
    description: 'Visual one-pager defining the ideal customer profile for the beachhead segment.',
    assignedAgentId: 'designer',
    starterPrompt:
      'Help me draft a one-pager for my Ideal Customer Profile in the beachhead segment. Concrete attributes only: industry, size, role, pain trigger, current alternative, budget owner. Save as agent-data/designer/icp.md.',
    required: true,
    deliverables: ['designer/icp.md'],
    xp: 250,
    source: 'phase-template',
  },
  {
    id: 'p4-positioning-statement',
    era: 4,
    mgtPhase: 7,
    title: 'Sign positioning statement',
    description: 'Lock the positioning statement that will drive every outbound message.',
    assignedAgentId: 'designer',
    starterPrompt:
      'Read the locked value proposition at agent-data/scribe/value-proposition.md and derive the positioning statement from it — same Phase 6 template (For X who faces Y, our Z helps them W by mechanism, unlike alternative, because differentiator), sharpened for outbound use. Flag any drift from the locked version instead of silently rewriting it. Save as agent-data/designer/positioning.md.',
    required: true,
    deliverables: ['designer/positioning.md'],
    xp: 250,
    source: 'phase-template',
  },
  {
    id: 'p4-outreach-sequence',
    era: 4,
    mgtPhase: 7,
    title: 'Build outreach sequence',
    description: '3-message sequence for warm intros + cold contacts. < 120 words each. Banned phrases enforced.',
    assignedAgentId: 'caravaneer',
    starterPrompt:
      'Build me a 3-message outreach sequence targeting my ICP. Personalized opener, one ask per message, no banned phrases ("I hope this finds you well", "Quick question", "Just following up"). Save as agent-data/caravaneer/campaigns/<date>-<segment>.md per your campaign format.',
    required: true,
    deliverables: ['caravaneer/campaigns/*.md'],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p4-runway-snapshot',
    era: 4,
    mgtPhase: 8,
    title: 'Lock runway snapshot',
    description: 'Cash discipline: confirm ≥6 months runway with pessimistic scenario before scaling commitments.',
    assignedAgentId: 'analyst',
    starterPrompt:
      'Compute my current runway under both optimistic and pessimistic scenarios. Save as agent-data/analyst/runway.json with months_remaining number. Flag if <6 months.',
    required: true,
    deliverables: ['analyst/runway.json'],
    xp: 250,
    source: 'phase-template',
  },
  {
    id: 'p4-three-pilots',
    era: 4,
    mgtPhase: 9,
    title: 'Negotiate 3 paid pilots',
    description: 'Pilot agreements meeting all 7 criteria: objective, success metric, duration, owner, data, budget, next step.',
    assignedAgentId: 'caravaneer',
    starterPrompt:
      'Help me negotiate paid pilots with 3 prospects from my outreach. For each, create a pilot file at agent-data/caravaneer/pilots/<name>.md with frontmatter: objective, success_metric, duration, owner, data_access, budget, next_step, status: active, paying: true.',
    required: true,
    deliverables: ['caravaneer/pilots/*.md'],
    xp: 1000,
    source: 'phase-template',
  },
  {
    id: 'p4-pilot-metrics',
    era: 4,
    mgtPhase: 9,
    title: 'Track pilot metrics',
    description: 'KPI dashboard for active pilots: time-to-value, weekly usage, willingness-to-pay signal.',
    assignedAgentId: 'analyst',
    starterPrompt:
      'Set up a metrics dashboard for my active pilots, continuing the baseline KPIs defined in agent-data/analyst/baseline-kpis.md (carry over each baseline number as the comparison point). Track time-to-value, weekly usage, mesurable gain, conversion pilot→contract. Each metric tied to a decision. Save as agent-data/analyst/dashboards/pilot-metrics.md.',
    required: true,
    deliverables: ['analyst/dashboards/pilot-metrics.md'],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p4-first-case-study',
    era: 4,
    mgtPhase: 9,
    title: 'Publish first case study',
    description: 'Turn the first successful pilot into a reference: outcome metrics first, technology last. GTM playbook step 5.',
    assignedAgentId: 'designer',
    starterPrompt:
      'Help me draft the first customer case study from my most advanced pilot in agent-data/caravaneer/pilots/. Structure: the customer, the pain, what we deployed, the measured outcome (numbers), and a verbatim quote. Outcome metrics first, technology last. Save as agent-data/designer/case-studies/<slug>.md.',
    required: true,
    deliverables: ['designer/case-studies/*.md'],
    xp: 500,
    source: 'phase-template',
  },

  // ── Era 5 — Empire (MGT-413 Phases 10-12: PMF + Chasm + Company building) ──
  {
    id: 'p5-pmf-snapshot',
    era: 5,
    mgtPhase: 10,
    title: 'Capture PMF snapshot',
    description: 'Retention 30d + customers who recommend + total customers + MRR. Source of truth for PMF claim.',
    assignedAgentId: 'analyst',
    starterPrompt:
      'Capture the current PMF snapshot: 30d retention (0..1), customers who recommend, total customers, monthly recurring revenue. Save as agent-data/analyst/pmf-snapshot.json (JSON object with retention_30d, customers_who_recommend, total_customers, monthly_recurring_revenue).',
    required: true,
    deliverables: ['analyst/pmf-snapshot.json'],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p5-expand-paying',
    era: 5,
    mgtPhase: 10,
    title: 'Expand to 5 paying customers',
    description: 'Escalate pilots and similar accounts up the energy ladder until ≥5 customers in the beachhead segment are paying.',
    assignedAgentId: 'caravaneer',
    starterPrompt:
      'Help me expand from my current paying customers to ≥5 paying in the same segment. For each pilot in agent-data/caravaneer/pilots/, name the current energy-ladder rung and the single next move (renewal, conversion, expansion, or warm intro into a similar account). Update each pilot file frontmatter (status, paying) as deals progress — the Era 5 gate counts files with paying: true.',
    required: true,
    deliverables: ['caravaneer/pilots/*.md'],
    xp: 1000,
    source: 'phase-template',
  },
  {
    id: 'p5-sales-playbook',
    era: 5,
    mgtPhase: 11,
    title: 'Document sales playbook',
    description: 'Reproducible sales motion: ICP signals, qualification questions, objection responses, close.',
    assignedAgentId: 'caravaneer',
    starterPrompt:
      'Help me document the sales playbook that worked for my first paying customers. ICP qualification, sequence, objection map, close mechanics. Save as agent-data/caravaneer/sales-playbook.md.',
    required: true,
    deliverables: ['caravaneer/sales-playbook.md'],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p5-standardize-onboarding',
    era: 5,
    mgtPhase: 11,
    title: 'Standardize onboarding',
    description: 'Onboarding flow that scales without founder in the loop. Time-to-first-value < N days.',
    assignedAgentId: 'tinkerer',
    starterPrompt:
      'Help me standardize the customer onboarding flow so it works without me in the loop. Document steps, automation gaps, time-to-first-value target. Save as agent-data/tinkerer/onboarding.md.',
    required: true,
    deliverables: ['tinkerer/onboarding.md'],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p5-case-studies',
    era: 5,
    mgtPhase: 11,
    title: 'Publish 2 case studies',
    description: 'Mainstream-ready references with outcome metrics, not technology. Chasm crossing prep.',
    assignedAgentId: 'designer',
    starterPrompt:
      'Help me draft 2 customer case studies. Mainstream language: outcome metrics first, technology last. References, support, ROI demonstrated. Save in agent-data/designer/case-studies/<slug>.md (one file per case study).',
    required: true,
    deliverables: ['designer/case-studies/*.md'],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p5-pitch-three',
    era: 5,
    mgtPhase: 11,
    title: 'Assemble Pitch 3 (business plan)',
    description:
      'The investor-grade story: traction, PMF evidence, repeatable sales motion, and the ask.',
    assignedAgentId: 'scribe',
    starterPrompt:
      'Assemble the final pitch (business plan) from analyst/pmf-snapshot.json, caravaneer/sales-playbook.md, designer/case-studies/ and caravaneer/pilots/. 6 sections: traction & PMF evidence, repeatable GTM, business model & unit economics, team/resources, risks, the ask. Save as agent-data/scribe/pitch-3.md. Then call generate_slide_deck to render the YC-minimal deck (pitch-1.pptx / pitch-2.pptx / pitch-3.pptx matching the pitch number).',
    required: true,
    deliverables: ['scribe/pitch-3.md'],
    xp: 500,
    source: 'phase-template',
  },
  {
    id: 'p5-horizons-plan',
    era: 5,
    mgtPhase: 12,
    title: 'Plan exploration vs exploitation',
    description:
      'Map the 3 horizons (defend core / build emerging / create options) and pick an ambidexterity set-up so growth does not depend on founder heroics.',
    assignedAgentId: 'overseer',
    starterPrompt:
      'Help me write a 3-horizons plan (Baghai/Coley/White): Horizon 1 defend & extend the core, Horizon 2 emerging businesses, Horizon 3 viable options/bets. Then choose an ambidexterity set-up (structural separate unit vs contextual %-time) and name what I stop doing to fund exploration. Save as agent-data/overseer/horizons-plan.md.',
    required: true,
    deliverables: ['overseer/horizons-plan.md'],
    xp: 500,
    source: 'phase-template',
  },
];

export const QUESTS: Quest[] = QUEST_LIST.map((q) => ({ ...q, done: false }));

export const QUESTS_BY_ID: Record<string, Quest> = Object.fromEntries(
  QUESTS.map((q) => [q.id, q]),
);
